import type { EChartsCoreOption } from "echarts/core";
import type { SpendReportTrendPoint } from "@/types/spend";

const COMPACT_GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  notation: "compact",
  maximumFractionDigits: 0,
});
const FULL_GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });

// ECharts paints on canvas, so its colours are literals rather than Tailwind tokens.
const SELECTED_COLOR = "#2b2523";
const OTHER_COLOR = "#d3cdc6";

export const buildReportTrendOption = (
  trend: SpendReportTrendPoint[],
  selectedMonth: string,
  idLabel: string,
  formatMonthYear: (month: string) => string,
): EChartsCoreOption => ({
  aria: { show: true },
  animationDuration: 250,
  grid: { left: 8, right: 8, top: 28, bottom: 8, containLabel: true },
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    formatter: (params: { dataIndex: number }[]) => {
      const point = trend[params[0]?.dataIndex ?? 0];
      if (!point) return "";
      return [
        formatMonthYear(point.month),
        `Spend: ${FULL_GBP.format(Number(point.spend_gbp))}`,
        `${idLabel}: ${point.id_count}`,
        `Transactions: ${point.transaction_count}`,
      ].join("<br/>");
    },
  },
  xAxis: {
    type: "category",
    data: trend.map(point => formatMonthYear(point.month)),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "#e4ddd5" } },
    axisLabel: { color: "#6b7280" },
  },
  yAxis: { type: "value", show: false },
  series: [
    {
      name: "Spend",
      type: "bar",
      barMaxWidth: 64,
      data: trend.map(point => ({
        value: Number(point.spend_gbp),
        itemStyle: { color: point.month === selectedMonth ? SELECTED_COLOR : OTHER_COLOR, borderRadius: [2, 2, 0, 0] },
      })),
      label: {
        show: true,
        position: "top",
        color: "#4b5563",
        fontSize: 11,
        formatter: ({ value }: { value: number }) => (value ? COMPACT_GBP.format(value) : ""),
      },
    },
  ],
});
